import { Component, forwardRef, Input, OnChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { provideTranslocoScope } from '@ngneat/transloco';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TypedChanges } from '@platform-frontend/util';
import { UI_TOOLS_TRANSLOCO_SCOPE } from '../../ui-tools-transloco-scope';
import { DateSelectionValue } from '../model/date-selection-value';

@UntilDestroy()
@Component({
  selector: 'yat-upcoming-month-selector',
  templateUrl: './upcoming-month-selector.component.html',
  styleUrls: ['./upcoming-month-selector.component.scss'],
  providers: [
    provideTranslocoScope(UI_TOOLS_TRANSLOCO_SCOPE),
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UpcomingMonthSelectorComponent),
      multi: true
    }
  ]
})
export class UpcomingMonthSelectorComponent implements ControlValueAccessor, OnChanges {
  @Input()
  id: string = '';

  @Input()
  currentDate: Date = new Date();

  @Input()
  includeCurrentMonth: boolean = false;

  @Input()
  includeMonthsAhead: number = 0;

  selectedMonth: number = 0;
  selectionOptions: Date[] = [];

  ngOnChanges(changes: TypedChanges<UpcomingMonthSelectorComponent>): void {
    if (changes.currentDate || changes.includeCurrentMonth || changes.includeMonthsAhead) {
      const selectedOption = this.selectionOptions.length > this.selectedMonth ? this.selectionOptions[this.selectedMonth] : null;
      this.buildSelectionOptions();
      this.selectMonth(selectedOption ? new Date(Date.UTC(selectedOption.getFullYear(), selectedOption.getMonth())) : null);
    }
  }

  private buildSelectionOptions(): void {
    this.selectionOptions = [];

    const currentMonth = this.currentDate.getMonth();
    const currentYear = this.currentDate.getFullYear();

    for (let i = this.includeCurrentMonth ? 0 : 1; i < this.includeMonthsAhead; i++) {
      const month = (currentMonth + i) % 12;
      const year = currentYear + Math.floor((currentMonth + i) / 12);
      this.selectionOptions.push(new Date(Date.UTC(year, month)));
    }
  }

  onChange: (newSelectedMonth: DateSelectionValue) => void;

  onTouched: () => void;

  registerOnChange(onChangeCallback: (newSelectedMonth: DateSelectionValue) => void): void {
    this.onChange = onChangeCallback;
  }

  registerOnTouched(onTouchedCallback: () => void): void {
    this.onTouched = onTouchedCallback;
  }

  writeValue(newDate: DateSelectionValue): void {
    this.selectMonth(newDate);
  }

  private selectMonth(newDate: DateSelectionValue): void {
    this.selectedMonth =
      newDate && newDate !== 'INVALID'
        ? this.selectionOptions.findIndex(option => option.getMonth() === newDate.getMonth() && option.getFullYear() === newDate.getFullYear())
        : 0;
  }

  onMonthSelect(selectedMonth: number): void {
    this.selectedMonth = selectedMonth;

    if (selectedMonth < 0 || selectedMonth >= this.selectionOptions.length) {
      this.onChange(null);
      return;
    }

    this.onChange(this.selectionOptions[selectedMonth]);
  }
}
